"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useModal } from "@/contexts/modal-context";
import { useActivateReferral } from "@/hooks/challenges";
import { Button } from "./ui/button";
import { Gift, X } from "lucide-react";

export const ReferralModal = () => {
  const { isReferralModalVisible, hideReferralModal } = useModal();
  const [value, setValue] = useState<string>("");
  const activateReferralMutation = useActivateReferral();

  useEffect(() => {
    if (isReferralModalVisible && typeof window !== "undefined") {
      const storedReferral = localStorage.getItem("referralCode");
      setValue(storedReferral || "");
    }
  }, [isReferralModalVisible]);

  const handleClose = () => {
    hideReferralModal();
    setValue("");
    if (typeof window !== "undefined") {
      localStorage.removeItem("referralCode");
    }
  };

  const handleActivate = async () => {
    if (value && value.trim()) {
      await activateReferralMutation.mutateAsync(value.trim());
      handleClose();
    }
  };

  if (!isReferralModalVisible) return null;

  const modalContent = (
    <AnimatePresence>
      {isReferralModalVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed top-1/2 left-1/2 w-full max-w-md bg-yiedl-dark border border-yiedl-gray-800 rounded-2xl z-[101] shadow-2xl mx-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-yiedl-gray-800">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-white">
                  Activate Referral
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-yiedl-gray-800 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-yiedl-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              <p className="text-sm text-yiedl-gray-400 text-center">
                Enter your referral code to unlock rewards and start earning
                points!
              </p>

              {/* Input */}
              <div className="space-y-2">
                <label
                  htmlFor="referral-code"
                  className="text-sm font-medium text-yiedl-gray-400"
                >
                  Referral Code
                </label>
                <input
                  id="referral-code"
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter referral code"
                  className="w-full px-4 py-3 bg-yiedl-gray-900/50 border border-yiedl-gray-800 rounded-xl text-white placeholder-yiedl-gray-500 focus:outline-none focus:border-yiedl-primary transition-colors"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && value && value.trim()) {
                      handleActivate();
                    }
                  }}
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={activateReferralMutation.isPending}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleActivate}
                  disabled={
                    !value ||
                    !value.trim() ||
                    activateReferralMutation.isPending
                  }
                  className="flex-1"
                >
                  {activateReferralMutation.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Activating...
                    </span>
                  ) : (
                    "Activate"
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return typeof window !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
